import {
  APIProvider,
  AdvancedMarker,
  Map,
  MapMouseEvent,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';
import {
  ShopInfoForApp,
  ShopInfoForAppPayload,
  createShopInfoForApp,
  deleteShopImageForApp,
  deleteShopLogoImageForApp,
  fetchShopInfoForApp,
  showShop,
  updateShopInfoForApp,
  uploadShopImageForApp,
  uploadShopLogoImageForApp,
} from 'admin/actions/shops';
import Button from 'admin/components/atoms/Buttons';
import FA from 'admin/components/atoms/FontAwesome';
import Grid from 'admin/components/atoms/Grid';
import Headings from 'admin/components/atoms/Headings';
import Label from 'admin/components/atoms/Label';
import Radio from 'admin/components/atoms/Radio';
import TextArea from 'admin/components/atoms/TextArea';
import TextBox from 'admin/components/atoms/TextBox';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import { useAppSelector } from 'admin/hooks/useApp';
import cameraIcon from 'admin/icons/camera.svg';
import mapPinIcon from 'admin/icons/map-pin.svg';
import imagePlaceholder from 'admin/icons/shop-image-placeholder.svg';
import logoPlaceholder from 'admin/icons/shop-logo-placeholder.svg';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { RouteComponentProps } from 'react-router-dom';
import styled, { css } from 'styled-components';
import Required from '../../atoms/Required';

const GOOGLE_MAPS_API_KEY = window.GOOGLE_MAPS_API_KEY ?? '';
const DEFAULT_CENTER = { lat: 35.6762, lng: 139.6503 };
const IMAGE_BUTTON_LABELS = [
  'メインの画像をアップロード',
  '2枚目の画像をアップロード',
  '3枚目の画像をアップロード',
  '4枚目の画像をアップロード',
] as const;

type ShopData = Awaited<ReturnType<typeof showShop>>;
type ShopImage = { image_url: string; id: number };
type PendingFile = { file: File; previewUrl: string };

// 新規登録時は店舗表示を未選択 (null) から開始するため、フォーム内部の型でのみ null を許容する。
// API 契約 (ShopInfoForAppPayload) は display_on_app: boolean のまま変えない。
type ShopAppFormValues = Omit<ShopInfoForAppPayload, 'display_on_app'> & {
  display_on_app: boolean | null;
};

const toFormValues = (info: ShopInfoForApp): ShopAppFormValues => ({
  display_on_app: info.display_on_app ?? false,
  latitude: info.latitude ?? null,
  longitude: info.longitude ?? null,
  business_hours: info.business_hours ?? '',
  regular_closed_days: info.regular_closed_days ?? '',
  url: info.url ?? '',
  description: info.description ?? '',
});

const PageWrapper = styled.div``;

const FormGrid = styled(Grid.Container)`
  margin: 16px 0;

  ${Label} {
    margin-bottom: 0;
  }

  ${Grid.Divider} {
    margin: 1rem 0;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 1rem;
`;

const RadioWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const LatLngRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 1rem;
`;

const MapWrapper = styled.div`
  width: 100%;
  max-width: 640px;
  height: 380px;
  margin-bottom: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
`;

const ImageSlot = styled.div<{ logo?: boolean }>`
  position: relative;
  width: ${({ logo }) => (logo ? '60px' : '160px')};
  height: ${({ logo }) => (logo ? '60px' : '80px')};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SlotImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlaceholderImage = styled.img`
  width: 100%;
  height: 100%;
`;

const ImageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 1rem;
`;

const LogoUploadRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 1rem;
`;

const LogoUploadControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  ${Button} {
    width: 280px;
  }
`;

const LogoFileRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  font-size: 13px;
  color: #444;
`;

const ImageHelpText = styled.p`
  ${({ theme: { colors, borderRadius, typography } }) => css`
    ${typography.body3.regular}
    background: ${colors.gray['50']};
    border-radius: ${borderRadius['8']};
    padding: 8px 16px;
    margin: 0;
    max-width: 468px;
  `}
`;

const TrashButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #888;
  padding: 0 2px;
  line-height: 1;
  &:hover {
    color: #d32f2f;
  }
`;

const Alert = styled.div<{ isError?: boolean }>`
  padding: 10px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
  background: ${({ isError }) => (isError ? '#fdecea' : '#e8f5e9')};
  color: ${({ isError }) => (isError ? '#c62828' : '#2e7d32')};
  border: 1px solid ${({ isError }) => (isError ? '#ef9a9a' : '#a5d6a7')};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const UploadButton = styled(Button)`
  display: flex;
  gap: 16px;
`;

const FieldHint = styled.p`
  ${({ theme: { colors, typography } }) => css`
    ${typography.body3.regular}
    color: ${colors.gray['400']};
    margin: -8px 0 8px;
  `}
`;

const ReadOnlyValue = styled.div`
  margin-bottom: 1rem;
`;

const FieldError = styled.p`
  ${({ theme: { semanticColors, typography } }) => css`
    ${typography.body2.regular}
    color: ${semanticColors.text.error};
    margin: -8px 0 8px;
  `}
`;

const ShopAppContent = ({ match }: RouteComponentProps<{ id: string }>) => {
  const shopId = match.params.id;

  const [shop, setShop] = useState<ShopData | null>(null);
  const [infoExists, setInfoExists] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null);

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoFileName, setLogoFileName] = useState<string | null>(null);
  const [shopImages, setShopImages] = useState<(ShopImage | null)[]>([null, null, null, null]);
  const [imageFileNames, setImageFileNames] = useState<(string | null)[]>([null, null, null, null]);

  const [pendingLogo, setPendingLogo] = useState<PendingFile | null>(null);
  const [logoDeleted, setLogoDeleted] = useState(false);
  const [logoSizeError, setLogoSizeError] = useState<string | null>(null);
  const [imageSizeErrors, setImageSizeErrors] = useState<(string | null)[]>([null, null, null, null]);
  const [pendingImages, setPendingImages] = useState<(PendingFile | null)[]>([
    null,
    null,
    null,
    null,
  ]);
  const [deletedImageIds, setDeletedImageIds] = useState<(number | null)[]>([
    null,
    null,
    null,
    null,
  ]);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const imageInputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null]);

  const geocodingLib = useMapsLibrary('geocoding');
  const map = useMap();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ShopAppFormValues>({
    defaultValues: {
      display_on_app: null,
      latitude: null,
      longitude: null,
      business_hours: '',
      regular_closed_days: '',
      url: '',
      description: '',
    },
  });

  const [watchedLat, watchedLng, watchedDisplayOnApp] = watch([
    'latitude',
    'longitude',
    'display_on_app',
  ]);

  // 店舗表示 (display_on_app) はアプリ掲載可否そのものなので、この店舗が加盟しているマネーを
  // 発行している組織のユーザーだけが操作できる。
  // 判定にヘッダーで選択中のマネーは使わない。shop_info_for_app は店舗単位の 1 レコードなのに対し
  // ヘッダーの選択は店舗と無関係に切り替わるため、同じ店舗の同じレコードに対して判定が揺れる
  // (発行体が他社マネー選択中に非表示を書き込む / 自社マネーを持つ加盟事業者がガードを外せる)。
  const user = useAppSelector((state) => state.user);
  const isIssuerOfThisShop = (shop?.accounts ?? []).some(
    (account) => account.private_money?.organization?.code === user.organization?.code
  );
  const isMerchantUser = !isIssuerOfThisShop;
  // 店舗が取得できていないと発行体かどうかを判定できない。この状態で保存させると
  // display_on_app を無自覚に false で書き込んでしまうため、保存自体を止める。
  const canJudgeIssuer = shop != null;

  useEffect(() => {
    Promise.all([
      showShop(shopId).then(setShop),
      fetchShopInfoForApp(shopId).then((info) => {
        if (info) {
          setInfoExists(true);
          reset(toFormValues(info));
          const url = info.logo_image_url ?? null;
          setLogoUrl(url);
          if (url) {
            try {
              setLogoFileName(decodeURIComponent(new URL(url).pathname.split('/').pop() ?? ''));
            } catch {
              setLogoFileName(url.split('/').pop()?.split('?')[0] ?? null);
            }
          }
          const slots: (ShopImage | null)[] = [null, null, null, null];
          const fileNames: (string | null)[] = [null, null, null, null];
          (info.image_urls ?? []).forEach((img, i) => {
            if (i < 4 && img.image_url != null && img.id != null) {
              slots[i] = { image_url: img.image_url, id: img.id };
              try {
                fileNames[i] = decodeURIComponent(
                  new URL(img.image_url).pathname.split('/').pop() ?? ''
                );
              } catch {
                fileNames[i] = img.image_url.split('/').pop()?.split('?')[0] ?? null;
              }
            }
          });
          setShopImages(slots);
          setImageFileNames(fileNames);
        }
      }),
    ])
      .catch(() => undefined)
      .finally(() => setIsLoading(false));
  }, [shopId, reset]);

  const onSubmit = async (data: ShopAppFormValues) => {
    if (!canJudgeIssuer) return;
    setIsSaving(true);
    setMessage(null);
    try {
      if (logoDeleted && logoUrl) {
        await deleteShopLogoImageForApp(shopId);
        setLogoUrl(null);
        setLogoFileName(null);
        setLogoDeleted(false);
      }
      if (pendingLogo) {
        const formData = new FormData();
        formData.append('logo_image', pendingLogo.file);
        formData.append('_csrf_token', window._csrf_token ?? '');
        const result = await uploadShopLogoImageForApp(shopId, formData);
        setLogoUrl(result.logo_url ?? null);
        setLogoFileName(pendingLogo.file.name);
        URL.revokeObjectURL(pendingLogo.previewUrl);
        setPendingLogo(null);
        setLogoDeleted(false);
      }

      await Promise.all(
        [0, 1, 2, 3].map(async (i) => {
          const deletedId = deletedImageIds[i];
          if (deletedId != null) {
            await deleteShopImageForApp(shopId, deletedId);
            setShopImages((prev) => {
              const n = [...prev] as (ShopImage | null)[];
              n[i] = null;
              return n;
            });
            setImageFileNames((prev) => {
              const n = [...prev] as (string | null)[];
              n[i] = null;
              return n;
            });
            setDeletedImageIds((ids) => {
              const n = [...ids];
              n[i] = null;
              return n;
            });
          }
          const pImg = pendingImages[i];
          if (pImg) {
            const formData = new FormData();
            formData.append('image', pImg.file);
            formData.append('name', pImg.file.name.slice(0, 16));
            formData.append('_csrf_token', window._csrf_token ?? '');
            const result = await uploadShopImageForApp(shopId, formData);
            // The endpoint returns a single image object { image_url, id },
            // not the { images: [] } shape the generated type declares.
            const newImage = result as unknown as { image_url?: string; id?: number };
            const imageUrl = newImage?.image_url;
            const imageId = newImage?.id;
            if (imageUrl != null && imageId != null) {
              setShopImages((prev) => {
                const n = [...prev] as (ShopImage | null)[];
                n[i] = { image_url: imageUrl, id: imageId };
                return n;
              });
            }
            setImageFileNames((prev) => {
              const n = [...prev] as (string | null)[];
              n[i] = pImg.file.name;
              return n;
            });
            URL.revokeObjectURL(pImg.previewUrl);
            setPendingImages((imgs) => {
              const n = [...imgs];
              n[i] = null;
              return n;
            });
          }
        })
      );

      const { display_on_app: displayOnApp, ...rest } = data;
      const base = { ...rest, url: data.url || null };
      // 加盟事業者ユーザーは店舗表示を変更できない。
      // - 更新: display_on_app を送らない (発行体が変更した値を上書きしないため)
      // - 新規: POST は display_on_app が必須なので、サーバ既定と同じ false で作成する
      const result = infoExists
        ? await updateShopInfoForApp(
            shopId,
            isMerchantUser ? base : { ...base, display_on_app: displayOnApp === true }
          )
        : await createShopInfoForApp(shopId, {
            ...base,
            display_on_app: isMerchantUser ? false : displayOnApp === true,
          });
      setInfoExists(true);
      reset(toFormValues(result));
      setMessage({ text: 'アプリ情報を保存しました', isError: false });
    } catch {
      setMessage({ text: '保存に失敗しました。', isError: true });
    } finally {
      setIsSaving(false);
    }
  };

  const onMarkerDragEnd = (e: any) => {
    if (!e.latLng) return;
    setValue('latitude', e.latLng.lat());
    setValue('longitude', e.latLng.lng());
  };

  const onMapClick = (e: MapMouseEvent) => {
    if (!e.detail.latLng) return;
    setValue('latitude', e.detail.latLng!.lat);
    setValue('longitude', e.detail.latLng!.lng);
  };

  const geocodeAddress = () => {
    if (!geocodingLib || !shop?.address) return;
    const geocoder = new geocodingLib.Geocoder();
    geocoder.geocode({ address: shop.address }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const loc = results[0].geometry.location;
        setValue('latitude', loc.lat());
        setValue('longitude', loc.lng());
        map?.panTo({ lat: loc.lat(), lng: loc.lng() });
        map?.setZoom(15);
      }
    });
  };

  const onLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > 2 * 1024 * 1024) {
      setLogoSizeError('ロゴ画像は2MB以下のファイルを選択してください。');
      return;
    }
    setLogoSizeError(null);
    if (pendingLogo) URL.revokeObjectURL(pendingLogo.previewUrl);
    if (logoUrl) setLogoDeleted(true);
    setPendingLogo({ file, previewUrl: URL.createObjectURL(file) });
  };

  const onLogoDelete = () => {
    if (pendingLogo) {
      URL.revokeObjectURL(pendingLogo.previewUrl);
      setPendingLogo(null);
      setLogoDeleted(false);
    } else if (logoUrl) {
      setLogoDeleted(true);
    }
  };

  const onImageFileChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > 5 * 1024 * 1024) {
      setImageSizeErrors((errs) => {
        const n = [...errs];
        n[index] = '写真は5MB以下のファイルを選択してください。';
        return n;
      });
      return;
    }
    setImageSizeErrors((errs) => {
      const n = [...errs];
      n[index] = null;
      return n;
    });
    const prev = pendingImages[index];
    if (prev) URL.revokeObjectURL(prev.previewUrl);
    const existingImage = shopImages[index];
    if (existingImage && deletedImageIds[index] == null) {
      setDeletedImageIds((ids) => {
        const n = [...ids];
        n[index] = existingImage.id;
        return n;
      });
    }
    setPendingImages((imgs) => {
      const n = [...imgs];
      n[index] = { file, previewUrl: URL.createObjectURL(file) };
      return n;
    });
  };

  const onImageDelete = (index: number) => {
    const pending = pendingImages[index];
    if (pending) {
      URL.revokeObjectURL(pending.previewUrl);
      setPendingImages((imgs) => {
        const n = [...imgs];
        n[index] = null;
        return n;
      });
      setDeletedImageIds((ids) => {
        const n = [...ids];
        n[index] = null;
        return n;
      });
    } else {
      const existing = shopImages[index];
      if (existing) {
        setDeletedImageIds((ids) => {
          const n = [...ids];
          n[index] = existing.id;
          return n;
        });
      }
    }
  };

  const logoDisplayUrl = pendingLogo?.previewUrl ?? (logoDeleted ? null : logoUrl);
  const logoDisplayFileName = pendingLogo?.file.name ?? (logoDeleted ? null : logoFileName);
  const imageDisplayUrl = (i: number) =>
    pendingImages[i]?.previewUrl ??
    (deletedImageIds[i] != null ? null : shopImages[i]?.image_url ?? null);
  const imageDisplayFileName = (i: number) =>
    pendingImages[i]?.file.name ?? (deletedImageIds[i] != null ? null : imageFileNames[i]);

  const CRUMBS = [
    { value: 'ホーム', href: '/', isCurrentPage: false },
    { value: '店舗管理', href: '', isCurrentPage: true },
    { value: '店舗一覧', href: '/shops', isCurrentPage: false },
    { value: '店舗詳細', href: `/shops/${shopId}`, isCurrentPage: false },
    { value: 'アプリ情報編集', href: '', isCurrentPage: true },
  ];

  const mapCenter =
    watchedLat != null && watchedLng != null
      ? { lat: watchedLat, lng: watchedLng }
      : DEFAULT_CENTER;
  const mapZoom = watchedLat != null ? 15 : 5;

  if (isLoading) {
    return (
      <PageWrapper>
        <BreadCrumb crumbs={CRUMBS} />
        <p>読み込み中...</p>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      <Headings.h5>アプリ情報編集</Headings.h5>

      <FormGrid>
        <Grid.Item size={2}>基本情報</Grid.Item>
        <Grid.Item size={6} flexDirection="column" gap="12px">
          <Grid.Item flexDirection="column">
            <Label>店舗ID</Label>
            <div>{shopId}</div>
          </Grid.Item>
          <Grid.Item flexDirection="column">
            <Label>店舗名</Label>
            <TextBox syncValue={shop?.name ?? ''} disabled fullWidth />
          </Grid.Item>
          <Grid.Item flexDirection="column">
            <Label>郵便番号</Label>
            <TextBox syncValue={shop?.postal_code ?? ''} disabled fullWidth />
          </Grid.Item>
          <Grid.Item flexDirection="column">
            <Label>住所</Label>
            <TextBox syncValue={shop?.address ?? ''} disabled fullWidth />
          </Grid.Item>
        </Grid.Item>

        <Grid.Divider />

        <Grid.Item size={2}>アプリ情報</Grid.Item>
        <Grid.Item size={8} flexDirection="column" gap="12px">
          <Grid.Item flexDirection="column">
            <Label>
              店舗表示 {!isMerchantUser && <Required />}
            </Label>
            {isMerchantUser ? (
              <>
                <ReadOnlyValue>
                  {watchedDisplayOnApp === null
                    ? '未設定'
                    : watchedDisplayOnApp
                    ? '表示する'
                    : '表示しない'}
                </ReadOnlyValue>
                <FieldHint>
                  {watchedDisplayOnApp === null
                    ? '店舗表示は発行体のみ設定できます。設定されるまでアプリには表示されません。'
                    : '店舗表示は発行体のみ変更できます。'}
                </FieldHint>
              </>
            ) : (
              <>
                <Controller
                  name="display_on_app"
                  control={control}
                  rules={{
                    validate: (value) => value !== null || '店舗表示を選択してください',
                  }}
                  render={({ field }) => (
                    <RadioGroup>
                      <RadioWrapper>
                        <Radio
                          name="display_on_app"
                          checked={field.value === true}
                          setChecked={() => field.onChange(true)}
                        />
                        <Label>表示する</Label>
                      </RadioWrapper>
                      <RadioWrapper>
                        <Radio
                          name="display_on_app"
                          checked={field.value === false}
                          setChecked={() => field.onChange(false)}
                        />
                        <Label>表示しない</Label>
                      </RadioWrapper>
                    </RadioGroup>
                  )}
                />
                {errors.display_on_app && (
                  <FieldError role="alert">{errors.display_on_app.message}</FieldError>
                )}
              </>
            )}
          </Grid.Item>

          <Grid.Item flexDirection="column">
            <Label>経度・緯度</Label>
            <LatLngRow>
              <Controller
                name="latitude"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value == null && watch('longitude') != null) {
                      return '緯度・経度は両方入力してください';
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <TextBox
                    syncValue={field.value?.toString() ?? ''}
                    type="number"
                    step="any"
                    placeholder="緯度"
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))
                    }
                  />
                )}
              />
              ー
              <Controller
                name="longitude"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value == null && watch('latitude') != null) {
                      return '緯度・経度は両方入力してください';
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <TextBox
                    syncValue={field.value?.toString() ?? ''}
                    type="number"
                    step="any"
                    placeholder="経度"
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))
                    }
                  />
                )}
              />
              <UploadButton type="button" variant="muted-outline" onClick={geocodeAddress}>
                <img src={mapPinIcon} alt="" />
                住所から位置情報を取得
              </UploadButton>
            </LatLngRow>
            {errors.latitude || errors.longitude ? (
              <FieldError>{errors.latitude?.message ?? errors.longitude?.message}</FieldError>
            ) : (
              (watchedLat == null || watchedLng == null) && (
                <FieldHint>
                  未入力の場合、店舗リストには表示されますが地図には表示されません。
                </FieldHint>
              )
            )}
          </Grid.Item>

          <Grid.Item>
            <MapWrapper>
              <Map
                style={{ width: '100%', height: '100%' }}
                defaultCenter={mapCenter}
                defaultZoom={mapZoom}
                mapId="shop-app-map"
                onClick={onMapClick}
              >
                {watchedLat != null && watchedLng != null && (
                  <AdvancedMarker
                    position={{ lat: watchedLat, lng: watchedLng }}
                    draggable
                    onDragEnd={onMarkerDragEnd}
                  />
                )}
              </Map>
            </MapWrapper>
          </Grid.Item>

          <Grid.Item flexDirection="column">
            <Label>ロゴ画像</Label>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/jpeg,image/png"
              style={{ display: 'none' }}
              onChange={onLogoFileChange}
            />
            <LogoUploadRow>
              <ImageSlot logo>
                {logoDisplayUrl ? (
                  <SlotImage src={logoDisplayUrl} alt="ロゴ" />
                ) : (
                  <PlaceholderImage src={logoPlaceholder} alt="" />
                )}
              </ImageSlot>
              <LogoUploadControls>
                {logoDisplayFileName && (
                  <LogoFileRow>
                    <span>{logoDisplayFileName}</span>
                    <TrashButton type="button" onClick={onLogoDelete}>
                      <FA icon="trash" />
                    </TrashButton>
                  </LogoFileRow>
                )}
                <UploadButton
                  type="button"
                  variant="muted-outline"
                  onClick={() => logoInputRef.current?.click()}
                >
                  <img src={cameraIcon} alt="" />
                  画像をアップロード
                </UploadButton>
                {logoSizeError && <FieldError>{logoSizeError}</FieldError>}
              </LogoUploadControls>
            </LogoUploadRow>
            <ImageHelpText>
              最大2MBまでのJPG,PNGファイルを使用できます。推奨サイズは300×300pxです
            </ImageHelpText>
          </Grid.Item>

          <Grid.Item flexDirection="column">
            <Label>写真</Label>
            <ImageStack>
              {IMAGE_BUTTON_LABELS.map((label, index) => {
                const slotUrl = imageDisplayUrl(index);
                const slotFileName = imageDisplayFileName(index);
                return (
                <LogoUploadRow key={index}>
                  <input
                    ref={(el) => {
                      imageInputRefs.current[index] = el;
                    }}
                    type="file"
                    accept="image/jpeg,image/png"
                    style={{ display: 'none' }}
                    onChange={(e) => onImageFileChange(index, e)}
                  />
                  <ImageSlot>
                    {slotUrl ? (
                      <SlotImage src={slotUrl} alt={`店舗写真${index + 1}`} />
                    ) : (
                      <PlaceholderImage src={imagePlaceholder} alt="" />
                    )}
                  </ImageSlot>
                  <LogoUploadControls>
                    {slotFileName && (
                      <LogoFileRow>
                        <span>{slotFileName}</span>
                        <TrashButton type="button" onClick={() => onImageDelete(index)}>
                          <FA icon="trash" />
                        </TrashButton>
                      </LogoFileRow>
                    )}
                    <UploadButton
                      type="button"
                      variant="muted-outline"
                      onClick={() => imageInputRefs.current[index]?.click()}
                    >
                      <img src={cameraIcon} alt="" />
                      {label}
                    </UploadButton>
                    {imageSizeErrors[index] && <FieldError>{imageSizeErrors[index]}</FieldError>}
                  </LogoUploadControls>
                </LogoUploadRow>
                );
              })}
            </ImageStack>
            <ImageHelpText>1画像につき最大5MBまでのJPG,PNGファイルを使用できます</ImageHelpText>
          </Grid.Item>

          <Grid.Item flexDirection="column">
            <Label>営業時間</Label>
            <Controller
              name="business_hours"
              control={control}
              render={({ field }) => (
                <TextArea
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  rows={3}
                  placeholder={`例）月~金曜日\n        11:00~14:30\n        17:30~20:00`}
                  maxLength={200}
                />
              )}
            />
          </Grid.Item>

          <Grid.Item flexDirection="column">
            <Label>定休日</Label>
            <Controller
              name="regular_closed_days"
              control={control}
              render={({ field }) => (
                <TextArea
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  rows={3}
                  placeholder="例）第2・3火曜日休み"
                  maxLength={100}
                />
              )}
            />
          </Grid.Item>

          <Grid.Item flexDirection="column">
            <Label>店舗URL</Label>
            <Controller
              name="url"
              control={control}
              render={({ field }) => (
                <TextBox
                  syncValue={field.value ?? ''}
                  type="url"
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  fullWidth
                  placeholder="https://"
                />
              )}
            />
          </Grid.Item>

          <Grid.Item flexDirection="column">
            <Label>店舗紹介</Label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextArea
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  fullWidth
                  maxLength={200}
                />
              )}
            />
          </Grid.Item>
        </Grid.Item>

        <Grid.Divider />
      </FormGrid>

      {message && <Alert isError={message.isError}>{message.text}</Alert>}

      <ButtonGroup>
        <Button onClick={handleSubmit(onSubmit)} disabled={isSaving || !canJudgeIssuer}>
          保存
        </Button>
      </ButtonGroup>
      {!canJudgeIssuer && (
        <FieldError role="alert">
          店舗情報を取得できなかったため保存できません。再読み込みしてください。
        </FieldError>
      )}
    </PageWrapper>
  );
};

const ShopApp = (props: RouteComponentProps<{ id: string }>) => (
  <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
    <ShopAppContent {...props} />
  </APIProvider>
);

export default ShopApp;
